import { useQuery } from '@tanstack/react-query';

import {
  getChannelDailyReport,
  getFinancialIssues,
  getFinancialReport,
  getFinancialTransactions,
  getOperationalReport,
  getPartnerShareReport,
  getPricingReport,
  getProviderReport,
  getReportDenominationOptions,
  getReportFilterOptions,
} from './reports.api';
import type { FinancialTransactionParams, ReportParams } from '../types/report.types';

export const reportKeys = {
  all: ['reports'] as const,
  operational: (params: ReportParams) => [...reportKeys.all, 'operational', params] as const,
  financial: (params: ReportParams) => [...reportKeys.all, 'financial', params] as const,
  financialIssues: (params: ReportParams) =>
    [...reportKeys.all, 'financial-issues', params] as const,
  channelDaily: (params: ReportParams) =>
    [...reportKeys.all, 'financial-channels-daily', { ...params, groupBy: 'day' }] as const,
  partnerShare: (params: ReportParams) =>
    [
      ...reportKeys.all,
      'financial-partner-share',
      { ...params, groupBy: params.groupBy === 'month' ? 'month' : 'day' },
    ] as const,
  pricing: (params: ReportParams) => [...reportKeys.all, 'pricing', params] as const,
  providers: (params: ReportParams) => [...reportKeys.all, 'providers', params] as const,
  transactions: (params: FinancialTransactionParams) =>
    [...reportKeys.all, 'financial-transactions', params] as const,
  filterOptions: (countryId: number | undefined) =>
    [...reportKeys.all, 'filters', countryId ?? 'all'] as const,
  denominationOptions: (brandId: number, countryId: number | undefined) =>
    [...reportKeys.all, 'filters', 'denominations', brandId, countryId ?? 'all'] as const,
};

export function useOperationalReport(params: ReportParams, enabled = true) {
  return useQuery({
    queryKey: reportKeys.operational(params),
    queryFn: ({ signal }) => getOperationalReport(params, signal),
    placeholderData: (previous) => previous,
    enabled,
  });
}

export function useFinancialReport(params: ReportParams, enabled = true) {
  return useQuery({
    queryKey: reportKeys.financial(params),
    queryFn: ({ signal }) => getFinancialReport(params, signal),
    placeholderData: (previous) => previous,
    enabled,
  });
}

export function useFinancialIssues(params: ReportParams, enabled = true) {
  return useQuery({
    queryKey: reportKeys.financialIssues(params),
    queryFn: ({ signal }) => getFinancialIssues(params, signal),
    placeholderData: (previous) => previous,
    enabled,
  });
}

/** Each day on each payment channel; the grouping does not apply (always by day). */
export function useChannelDailyReport(params: ReportParams, enabled = true) {
  return useQuery({
    queryKey: reportKeys.channelDaily(params),
    queryFn: ({ signal }) => getChannelDailyReport(params, signal),
    placeholderData: (previous) => previous,
    enabled,
  });
}

/** The partner's share by day, or by month when the report is grouped by month. */
export function usePartnerShareReport(params: ReportParams, enabled = true) {
  return useQuery({
    queryKey: reportKeys.partnerShare(params),
    queryFn: ({ signal }) => getPartnerShareReport(params, signal),
    placeholderData: (previous) => previous,
    enabled,
  });
}

export function usePricingReport(params: ReportParams, enabled = true) {
  return useQuery({
    queryKey: reportKeys.pricing(params),
    queryFn: ({ signal }) => getPricingReport(params, signal),
    placeholderData: (previous) => previous,
    enabled,
  });
}

export function useProviderReport(params: ReportParams, enabled = true) {
  return useQuery({
    queryKey: reportKeys.providers(params),
    queryFn: ({ signal }) => getProviderReport(params, signal),
    placeholderData: (previous) => previous,
    enabled,
  });
}

export function useFinancialTransactions(params: FinancialTransactionParams, enabled = true) {
  return useQuery({
    queryKey: reportKeys.transactions(params),
    queryFn: ({ signal }) => getFinancialTransactions(params, signal),
    placeholderData: (previous) => previous,
    enabled,
  });
}

/** Filter choices change rarely; they are kept for five minutes. */
export function useReportFilterOptions(countryId: number | undefined) {
  return useQuery({
    queryKey: reportKeys.filterOptions(countryId),
    queryFn: ({ signal }) => getReportFilterOptions(countryId, signal),
    staleTime: 5 * 60_000,
  });
}

/** Only fetched once a brand is chosen. */
export function useReportDenominationOptions(
  brandId: number | undefined,
  countryId: number | undefined,
) {
  return useQuery({
    queryKey: reportKeys.denominationOptions(brandId ?? 0, countryId),
    queryFn: ({ signal }) => getReportDenominationOptions(brandId ?? 0, countryId, signal),
    enabled: brandId !== undefined,
    staleTime: 5 * 60_000,
  });
}
